import { useLocalStorage } from '@vueuse/core'
import { useAuthStore } from '~/store/auth'
import type { ContainerResource } from '~/types/resources/container'
import type { DocumentResource } from '~/types/resources/document'
import type { ContainerOrderAction, OrderResource } from '~/types/resources/order'
import type { ProjectResource } from '~/types/resources/project'

const ORDER_WIZARD_SCHEMA_VERSION = 1
const ORDER_WIZARD_ACTIONS: ContainerOrderAction[] = ['swap_container', 'order_container', 'collect_container']
const ORDER_WIZARD_STEP_ROUTES = {
    1: '/orders/new/action',
    2: '/orders/new/containers',
    3: '/orders/new/summary',
} as const
export const ORDER_WIZARD_TOTAL_STEPS = Object.keys(ORDER_WIZARD_STEP_ROUTES).length

type OrderWizardStep = keyof typeof ORDER_WIZARD_STEP_ROUTES

export type OrderWizardSelectedContainer = {
    containerId: number
    groupKey: string | null
    containerCount: number
    notes: string | null
    documents: DocumentResource[]
}

export type OrderWizardContractorOrder = {
    groupKey: string
    contractorId: number
    date: string | null
    timeFrom: string | null
    timeTo: string | null
    notes: string | null
    documents: DocumentResource[]
}

type OrderWizardState = {
    schemaVersion: number
    authContextKey: string | null
    activeProjectId: number | null
    action: ContainerOrderAction | null
    selectedContainers: OrderWizardSelectedContainer[]
    contractorOrders: OrderWizardContractorOrder[]
    lastVisitedStep: number
}

const defaultState: OrderWizardState = {
    schemaVersion: ORDER_WIZARD_SCHEMA_VERSION,
    authContextKey: null,
    activeProjectId: null,
    action: 'swap_container',
    selectedContainers: [],
    contractorOrders: [],
    lastVisitedStep: 1,
}

function normalizeNullableString(value: unknown): string | null {
    return typeof value === 'string' ? value : null
}

function normalizePositiveInteger(value: unknown): number | null {
    if (typeof value !== 'number' || !Number.isInteger(value) || value <= 0) {
        return null
    }

    return value
}

function isValidStep(value: unknown): value is OrderWizardStep {
    return typeof value === 'number'
        && Number.isInteger(value)
        && Object.prototype.hasOwnProperty.call(ORDER_WIZARD_STEP_ROUTES, value)
}

function normalizeDocuments(value: unknown): DocumentResource[] {
    return Array.isArray(value) ? value as DocumentResource[] : []
}

function normalizeSelectedContainers(value: unknown): OrderWizardState['selectedContainers'] {
    if (!Array.isArray(value)) {
        return []
    }

    const normalizedSelections = new Map<number, OrderWizardSelectedContainer>()

    value.forEach((entry) => {
        if (!entry || typeof entry !== 'object') {
            return
        }

        const containerId = normalizePositiveInteger((entry as { containerId?: unknown }).containerId)
        const containerCount = normalizePositiveInteger((entry as { containerCount?: unknown }).containerCount)

        if (!containerId || !containerCount) {
            return
        }

        normalizedSelections.set(containerId, {
            containerId,
            groupKey: normalizeNullableString((entry as { groupKey?: unknown }).groupKey),
            containerCount,
            notes: normalizeNullableString((entry as { notes?: unknown }).notes),
            documents: normalizeDocuments((entry as { documents?: unknown }).documents),
        })
    })

    return Array.from(normalizedSelections.values())
}

function cloneSelectedContainers(value: OrderWizardState['selectedContainers']): OrderWizardState['selectedContainers'] {
    return normalizeSelectedContainers(value).map((selection) => ({
        containerId: selection.containerId,
        groupKey: selection.groupKey,
        containerCount: selection.containerCount,
        notes: selection.notes,
        documents: (selection.documents ?? []).map((document) => ({ ...document })),
    }))
}

function cloneContractorOrders(value: OrderWizardState['contractorOrders']): OrderWizardState['contractorOrders'] {
    return normalizeContractorOrders(value).map((contractorOrder) => ({
        groupKey: contractorOrder.groupKey,
        contractorId: contractorOrder.contractorId,
        date: contractorOrder.date,
        timeFrom: contractorOrder.timeFrom,
        timeTo: contractorOrder.timeTo,
        notes: contractorOrder.notes,
        documents: (contractorOrder.documents ?? []).map((document) => ({ ...document })),
    }))
}

function normalizeContractorOrders(value: unknown): OrderWizardState['contractorOrders'] {
    if (!Array.isArray(value)) {
        return []
    }

    const normalizedSelections = new Map<string, OrderWizardContractorOrder>()

    value.forEach((entry) => {
        if (!entry || typeof entry !== 'object') {
            return
        }

        const contractorId = normalizePositiveInteger((entry as { contractorId?: unknown }).contractorId)
        const groupKey = normalizeNullableString((entry as { groupKey?: unknown }).groupKey)

        if (!contractorId || !groupKey) {
            return
        }

        normalizedSelections.set(groupKey, {
            groupKey,
            contractorId,
            date: normalizeNullableString((entry as { date?: unknown }).date),
            timeFrom: normalizeNullableString((entry as { timeFrom?: unknown }).timeFrom),
            timeTo: normalizeNullableString((entry as { timeTo?: unknown }).timeTo),
            notes: normalizeNullableString((entry as { notes?: unknown }).notes),
            documents: normalizeDocuments((entry as { documents?: unknown }).documents),
        })
    })

    return Array.from(normalizedSelections.values())
}

function isNullableString(value: unknown) {
    return value === null || typeof value === 'string'
}

function isValidSelectedContainers(value: unknown) {
    if (!Array.isArray(value)) {
        return false
    }

    const containerIds = new Set<number>()

    return value.every((entry) => {
        if (!entry || typeof entry !== 'object') {
            return false
        }

        const selection = entry as Partial<OrderWizardSelectedContainer>
        const containerId = normalizePositiveInteger(selection.containerId)
        const containerCount = normalizePositiveInteger(selection.containerCount)

        if (!containerId || !containerCount || containerIds.has(containerId)) {
            return false
        }

        containerIds.add(containerId)

        return isNullableString(selection.groupKey)
            && isNullableString(selection.notes)
            && Array.isArray(selection.documents)
    })
}

function isValidContractorOrders(value: unknown) {
    if (!Array.isArray(value)) {
        return false
    }

    const groupKeys = new Set<string>()

    return value.every((entry) => {
        if (!entry || typeof entry !== 'object') {
            return false
        }

        const contractorOrder = entry as Partial<OrderWizardContractorOrder>
        const contractorId = normalizePositiveInteger(contractorOrder.contractorId)
        const groupKey = normalizeNullableString(contractorOrder.groupKey)

        if (!contractorId || !groupKey || groupKeys.has(groupKey)) {
            return false
        }

        groupKeys.add(groupKey)

        return isNullableString(contractorOrder.date)
            && isNullableString(contractorOrder.timeFrom)
            && isNullableString(contractorOrder.timeTo)
            && isNullableString(contractorOrder.notes)
            && Array.isArray(contractorOrder.documents)
    })
}

function resetState(authContextKey: string | null = null): OrderWizardState {
    return {
        ...defaultState,
        authContextKey,
        selectedContainers: [],
        contractorOrders: [],
    }
}

function isCurrentSchemaState(value: unknown): value is Partial<OrderWizardState> {
    if (!value || typeof value !== 'object') {
        return false
    }

    return (value as { schemaVersion?: unknown }).schemaVersion === ORDER_WIZARD_SCHEMA_VERSION
}

function isValidCurrentStateShape(value: Partial<OrderWizardState>) {
    const authContextKey = value.authContextKey
    const activeProjectId = value.activeProjectId

    return (authContextKey === null || typeof authContextKey === 'string')
        && (activeProjectId === null || normalizePositiveInteger(activeProjectId) === activeProjectId)
        && ORDER_WIZARD_ACTIONS.includes(value.action as ContainerOrderAction)
        && isValidSelectedContainers(value.selectedContainers)
        && isValidContractorOrders(value.contractorOrders)
        && isValidStep(value.lastVisitedStep)
}

function normalizeState(value: Partial<OrderWizardState> | null | undefined): OrderWizardState {
    if (!isCurrentSchemaState(value) || !isValidCurrentStateShape(value)) {
        return resetState()
    }

    const rawState = value

    return {
        schemaVersion: ORDER_WIZARD_SCHEMA_VERSION,
        authContextKey: normalizeNullableString(rawState.authContextKey),
        activeProjectId: normalizePositiveInteger(rawState.activeProjectId),
        action: ORDER_WIZARD_ACTIONS.includes(rawState.action as ContainerOrderAction)
            ? rawState.action as ContainerOrderAction
            : defaultState.action,
        selectedContainers: normalizeSelectedContainers(rawState.selectedContainers),
        contractorOrders: normalizeContractorOrders(rawState.contractorOrders),
        lastVisitedStep: isValidStep(rawState.lastVisitedStep) ? rawState.lastVisitedStep : defaultState.lastVisitedStep,
    }
}

export const useOrderWizardStore = defineStore('orderWizard', () => {
    const { $apiRoute, $can } = useNuxtApp()
    const authStore = useAuthStore()
    const dayjs = useDayjs()
    const stepRoutes = ORDER_WIZARD_STEP_ROUTES

    const storedState = useLocalStorage<OrderWizardState>('orderWizard', resetState())

    const containers = ref<ContainerResource[]>([])
    const containersStatus = ref<'idle' | 'pending' | 'success' | 'error'>('idle')
    const lastContainersFetch = ref<{ organisationId: number | null; projectId: number | null }>({
        organisationId: null,
        projectId: null,
    })
    const activeProject = ref<ProjectResource | null>(null)
    const activeProjectStatus = ref<'idle' | 'pending' | 'success' | 'error'>('idle')
    const lastActiveProjectFetch = ref<{ organisationId: number | null; projectId: number | null }>({
        organisationId: null,
        projectId: null,
    })
    const isSubmitting = ref(false)

    storedState.value = normalizeState(storedState.value)

    const activeProjectId = computed({
        get: () => storedState.value.activeProjectId,
        set: (value) => {
            storedState.value.activeProjectId = value
        },
    })

    const action = computed({
        get: () => storedState.value.action,
        set: (value) => {
            storedState.value.action = value
        },
    })

    const selectedContainers = computed({
        get: () => storedState.value.selectedContainers,
        set: (value) => {
            storedState.value.selectedContainers = value
        },
    })

    const contractorOrders = computed({
        get: () => storedState.value.contractorOrders,
        set: (value) => {
            storedState.value.contractorOrders = value
        },
    })

    const lastVisitedStep = computed({
        get: () => storedState.value.lastVisitedStep ?? 1,
        set: (value) => {
            if (!isValidStep(value)) {
                storedState.value.lastVisitedStep = defaultState.lastVisitedStep
                return
            }

            storedState.value.lastVisitedStep = value
        },
    })

    const resolveStepFromRoute = (routePath: string): number | null => {
        const normalizedPath = routePath.split('?')[0]?.replace(/\/$/, '') || ''
        const matchedStep = Object.entries(stepRoutes)
            .find(([, route]) => normalizedPath === route)

        return matchedStep ? Number(matchedStep[0]) : null
    }

    const currentStep = computed(() => {
        const routePath = useRoute().path ?? ''
        return resolveStepFromRoute(routePath) ?? 1
    })

    const currentStepRoute = computed(() => getResumableRoute())

    const getRouteForStep = (step: number): string => {
        return isValidStep(step) ? stepRoutes[step] : stepRoutes[1]
    }

    function isStepReachable(step: number) {
        if (step === 1) {
            return true
        }

        if (!storedState.value.activeProjectId) {
            return false
        }

        if (step === 2) {
            return true
        }

        return step === 3 && (storedState.value.selectedContainers ?? []).length > 0
    }

    function getReachableStep(step: number) {
        const requestedStep = isValidStep(step) ? step : defaultState.lastVisitedStep

        for (let candidateStep = requestedStep; candidateStep > 1; candidateStep -= 1) {
            if (isStepReachable(candidateStep)) {
                return candidateStep
            }
        }

        return 1
    }

    function getResumableRoute() {
        return getRouteForStep(getReachableStep(lastVisitedStep.value))
    }

    function syncAuthUserData(activeProjectId: number | null) {
        if (!authStore.userData) {
            return
        }

        authStore.userData = {
            ...authStore.userData,
            active_project_id: activeProjectId,
        }

        if (import.meta.client) {
            localStorage.setItem('userData', JSON.stringify(authStore.userData))
        }
    }

    function resetProjectDependentState() {
        storedState.value.selectedContainers = []
        storedState.value.contractorOrders = []
        storedState.value.lastVisitedStep = 1
    }

    function resolveAuthContextKey(): string | null {
        const userId = normalizePositiveInteger(authStore.userData?.id ?? null)
        const organisationId = normalizePositiveInteger(authStore.userData?.organisation_id ?? null)

        if (userId && organisationId) {
            return [userId, organisationId].join(':')
        }

        if (import.meta.client && authStore.isAuthenticated == null) {
            const storedIsAuthenticated = localStorage.getItem('isAuthenticated')
            const storedUserData = localStorage.getItem('userData')

            if (storedIsAuthenticated === 'true' && storedUserData && !['undefined', 'null'].includes(storedUserData)) {
                try {
                    const parsedUserData = JSON.parse(storedUserData) as { id?: unknown; organisation_id?: unknown }
                    const persistedUserId = normalizePositiveInteger(parsedUserData.id)
                    const persistedOrganisationId = normalizePositiveInteger(parsedUserData.organisation_id)

                    if (persistedUserId && persistedOrganisationId) {
                        return [persistedUserId, persistedOrganisationId].join(':')
                    }
                } catch {
                    return storedState.value.authContextKey
                }
            }
        }

        if (authStore.isAuthenticated === false) {
            return null
        }

        return storedState.value.authContextKey
    }

    function resetTransientProjectData(organisationId: number | null, projectId: number | null) {
        activeProject.value = null
        activeProjectStatus.value = 'idle'
        lastActiveProjectFetch.value = { organisationId, projectId }

        containers.value = []
        containersStatus.value = 'idle'
        lastContainersFetch.value = { organisationId, projectId }
    }

    async function persistActiveProject(activeProjectId: number | null, silent = false) {
        return await $lara.put($apiRoute('spa.settings.activeProject'), {
            body: { active_project_id: activeProjectId },
            silent,
        })
    }

    async function clearInvalidActiveProject() {
        const organisationId = authStore.userData?.organisation_id ?? null

        resetProjectDependentState()
        storedState.value.activeProjectId = null
        syncAuthUserData(null)
        resetTransientProjectData(organisationId, null)
        await persistActiveProject(null, true)
    }

    function pruneSelectedContainers(availableContainers: ContainerResource[]) {
        const availableContainerCounts = new Map(
            availableContainers.map((container) => [container.id, container.count]),
        )

        const nextSelections = normalizeSelectedContainers(storedState.value.selectedContainers)
            .flatMap((selection) => {
                const maxCount = availableContainerCounts.get(selection.containerId)

                if (!maxCount) {
                    return []
                }

                return [{
                    containerId: selection.containerId,
                    groupKey: selection.groupKey,
                    containerCount: Math.min(selection.containerCount, maxCount),
                    notes: selection.notes,
                    documents: selection.documents ?? [],
                }]
            })

        const hasSelectionChanges = JSON.stringify(nextSelections) !== JSON.stringify(storedState.value.selectedContainers)

        if (!hasSelectionChanges) {
            return
        }

        storedState.value.selectedContainers = nextSelections

        if (nextSelections.length > 0) {
            return
        }

    }

    function resolveSelectedContractorOrderGroups(
        availableContainers: ContainerResource[],
        selections: OrderWizardState['selectedContainers'],
    ): Array<{ groupKey: string; contractorId: number }> {
        const selectedContainerIds = new Set(
            normalizeSelectedContainers(selections).map((selection) => selection.containerId),
        )
        const selectedContainersById = new Map(
            normalizeSelectedContainers(selections).map((selection) => [selection.containerId, selection]),
        )

        return availableContainers
            .filter((container) => selectedContainerIds.has(container.id))
            .map((container) => {
                const contractorId = normalizePositiveInteger(container.waste?.contractor_id ?? null)

                if (!contractorId) {
                    return null
                }

                const groupKey = selectedContainersById.get(container.id)?.groupKey

                return groupKey ? { groupKey, contractorId } : null
            })
            .filter((group): group is { groupKey: string; contractorId: number } => Boolean(group))
            .filter((group, index, groups) => groups.findIndex((entry) => entry.groupKey === group.groupKey) === index)
    }

    function pruneContractorOrders(groups: Array<{ groupKey: string }>) {
        const groupKeys = groups.map((group) => group.groupKey)

        const nextContractorOrders = normalizeContractorOrders(storedState.value.contractorOrders)
            .filter((contractorOrder) => groupKeys.includes(contractorOrder.groupKey))

        if (JSON.stringify(nextContractorOrders) === JSON.stringify(storedState.value.contractorOrders)) {
            return
        }

        storedState.value.contractorOrders = nextContractorOrders
    }

    function initFromAuth() {
        if (authStore.isAuthenticated == null) {
            return
        }

        storedState.value = normalizeState(storedState.value)
        const authContextKey = resolveAuthContextKey()

        if (storedState.value.authContextKey !== authContextKey) {
            resetWizard()
            storedState.value.authContextKey = authContextKey
        }

        const activeProjectIdFromAuth = normalizePositiveInteger(authStore.userData?.active_project_id ?? null)

        if (storedState.value.activeProjectId !== activeProjectIdFromAuth) {
            resetProjectDependentState()
            storedState.value.activeProjectId = activeProjectIdFromAuth
        }

        if (!storedState.value.action || !ORDER_WIZARD_ACTIONS.includes(storedState.value.action)) {
            storedState.value.action = defaultState.action
        }
    }

    async function setActiveProject(id: number | null) {
        const normalizedProjectId = normalizePositiveInteger(id)
        const previousProjectId = storedState.value.activeProjectId

        if (previousProjectId === normalizedProjectId) {
            return
        }

        const previousSelectedContainers = cloneSelectedContainers(storedState.value.selectedContainers ?? [])
        const previousContractorOrders = cloneContractorOrders(storedState.value.contractorOrders ?? [])

        resetProjectDependentState()

        storedState.value.activeProjectId = normalizedProjectId
        syncAuthUserData(normalizedProjectId)
        const response = await persistActiveProject(normalizedProjectId)

        if (!response?.error) {
            return
        }

        storedState.value.activeProjectId = previousProjectId
        storedState.value.selectedContainers = previousSelectedContainers
        storedState.value.contractorOrders = previousContractorOrders
        syncAuthUserData(previousProjectId)
    }

    function setAction(nextAction: ContainerOrderAction) {
        storedState.value.action = nextAction
    }

    function addContainer(containerId: number) {
        const hasContainer = (storedState.value.selectedContainers)
            .some((container) => container.containerId === containerId)

        if (hasContainer) {
            return
        }

        storedState.value.selectedContainers.push({
            containerId,
            containerCount: 1,
            notes: null,
            documents: [],
            groupKey: null,
        })
    }

    function removeContainer(containerId: number) {
        storedState.value.selectedContainers = (storedState.value.selectedContainers)
            .filter((container) => container.containerId !== containerId)
    }

    function setContainerCount(containerId: number, count: number, maxCount: number) {
        const clampedCount = Math.min(Math.max(count, 0), maxCount)
        const existingContainer = (storedState.value.selectedContainers)
            .find((container) => container.containerId === containerId)

        if (clampedCount === 0) {
            if (existingContainer) {
                removeContainer(containerId)
            }
            return
        }

        if (existingContainer) {
            existingContainer.containerCount = clampedCount
            return
        }

        storedState.value.selectedContainers = storedState.value.selectedContainers ?? []
            storedState.value.selectedContainers.push({
                containerId,
                groupKey: null,
                containerCount: clampedCount,
            notes: null,
            documents: [],
        })
    }

    function isContainerSelected(containerId: number) {
        return (storedState.value.selectedContainers ?? [])
            .some((container) => container.containerId === containerId)
    }

    function getContainerCount(containerId: number) {
        return (storedState.value.selectedContainers ?? [])
            .find((container) => container.containerId === containerId)?.containerCount ?? 0
    }

    function getSelectedContainer(containerId: number) {
        return (storedState.value.selectedContainers ?? [])
            .find((container) => container.containerId === containerId) ?? null
    }

    function getSelectedContractorOrder(groupKey: string) {
        return (storedState.value.contractorOrders ?? [])
            .find((contractorOrder) => contractorOrder.groupKey === groupKey) ?? null
    }

    function getContainerNotes(containerId: number) {
        return getSelectedContainer(containerId)?.notes ?? null
    }

    function getContainerDocuments(containerId: number) {
        return getSelectedContainer(containerId)?.documents ?? []
    }

    function getContractorOrderNotes(groupKey: string) {
        return getSelectedContractorOrder(groupKey)?.notes ?? null
    }

    function getContractorOrderDocuments(groupKey: string) {
        return getSelectedContractorOrder(groupKey)?.documents ?? []
    }

    function getContractorOrderDate(groupKey: string) {
        return getSelectedContractorOrder(groupKey)?.date ?? null
    }

    function getContractorOrderTimeFrom(groupKey: string) {
        return getSelectedContractorOrder(groupKey)?.timeFrom ?? null
    }

    function getContractorOrderTimeTo(groupKey: string) {
        return getSelectedContractorOrder(groupKey)?.timeTo ?? null
    }

    function setContainerDetails(containerId: number, payload: { notes?: string | null; documents?: DocumentResource[] }) {
        const selectedContainer = getSelectedContainer(containerId)

        if (!selectedContainer) {
            return
        }

        if (payload.notes !== undefined) {
            selectedContainer.notes = payload.notes
        }

        if (payload.documents !== undefined) {
            selectedContainer.documents = payload.documents
        }
    }

    function setContainerGroupKey(containerId: number, groupKey: string | null) {
        const selectedContainer = getSelectedContainer(containerId)

        if (!selectedContainer) {
            return
        }

        selectedContainer.groupKey = groupKey
    }

    function setContainerNotes(containerId: number, value: string | null) {
        setContainerDetails(containerId, { notes: value })
    }

    function setContainerDocuments(containerId: number, value: DocumentResource[]) {
        setContainerDetails(containerId, { documents: value })
    }

    function setContractorOrderDetails(groupKey: string, contractorId: number, payload: {
        date?: string | null
        timeFrom?: string | null
        timeTo?: string | null
        notes?: string | null
        documents?: DocumentResource[]
    }) {
        const normalizedContractorId = normalizePositiveInteger(contractorId)

        if (!normalizedContractorId || !groupKey) {
            return
        }

        let selectedContractorOrder = getSelectedContractorOrder(groupKey)

        if (!selectedContractorOrder) {
            selectedContractorOrder = {
                groupKey,
                contractorId: normalizedContractorId,
                date: null,
                timeFrom: null,
                timeTo: null,
                notes: null,
                documents: [],
            }

            storedState.value.contractorOrders.push(selectedContractorOrder)
        }

        if (payload.date !== undefined) {
            selectedContractorOrder.date = payload.date
        }

        if (payload.timeFrom !== undefined) {
            selectedContractorOrder.timeFrom = payload.timeFrom
        }

        if (payload.timeTo !== undefined) {
            selectedContractorOrder.timeTo = payload.timeTo
        }

        if (payload.notes !== undefined) {
            selectedContractorOrder.notes = payload.notes
        }

        if (payload.documents !== undefined) {
            selectedContractorOrder.documents = payload.documents
        }
    }

    function setContractorOrderNotes(groupKey: string, contractorId: number, value: string | null) {
        setContractorOrderDetails(groupKey, contractorId, { notes: value })
    }

    function setContractorOrderDocuments(groupKey: string, contractorId: number, value: DocumentResource[]) {
        setContractorOrderDetails(groupKey, contractorId, { documents: value })
    }

    function setContractorOrderDate(groupKey: string, contractorId: number, value: string | null) {
        setContractorOrderDetails(groupKey, contractorId, { date: value })
    }

    function setContractorOrderTimeFrom(groupKey: string, contractorId: number, value: string | null) {
        setContractorOrderDetails(groupKey, contractorId, { timeFrom: value })
    }

    function setContractorOrderTimeTo(groupKey: string, contractorId: number, value: string | null) {
        setContractorOrderDetails(groupKey, contractorId, { timeTo: value })
    }

    function containerHasNotesOrDocuments(containerId: number) {
        const selectedContainer = getSelectedContainer(containerId)

        if (!selectedContainer) {
            return false
        }

        return Boolean(selectedContainer.notes?.trim()) || (selectedContainer.documents?.length ?? 0) > 0
    }

    function contractorOrderHasNotesOrDocuments(groupKey: string) {
        const selectedContractorOrder = getSelectedContractorOrder(groupKey)

        if (!selectedContractorOrder) {
            return false
        }

        return Boolean(selectedContractorOrder.notes?.trim()) || (selectedContractorOrder.documents?.length ?? 0) > 0
    }

    const selectedContainerCount = computed(() => (storedState.value.selectedContainers ?? []).length)

    const selectedContainersTotalCount = computed(() => (storedState.value.selectedContainers ?? [])
        .reduce((total, container) => total + container.containerCount, 0))

    const selectedContractorOrderGroups = computed(() => resolveSelectedContractorOrderGroups(
        containers.value,
        storedState.value.selectedContainers,
    ))

    function resetWizard() {
        storedState.value = resetState(storedState.value.authContextKey)
    }

    async function submitOrder() {
        if (isSubmitting.value) {
            return null
        }

        const organisationId = authStore.userData?.organisation_id ?? null

        if (!organisationId || !storedState.value.activeProjectId) {
            return { error: true }
        }

        const ordersStoreRoute = $can('viewAny', 'organisations')
            ? 'spa.system.organisations.projects.orders.store'
            : 'spa.projects.organisations.projects.orders.store'

        if (!storedState.value.action) {
            return { error: true }
        }

        const contractorOrders = selectedContractorOrderGroups.value.map((group) => {
            const date = getContractorOrderDate(group.groupKey)
            const timeFrom = getContractorOrderTimeFrom(group.groupKey)
            const timeTo = getContractorOrderTimeTo(group.groupKey)

            return {
                group_key: group.groupKey,
                contractor_id: group.contractorId,
                time_from: date && timeFrom ? dayjs(`${date} ${timeFrom}`).toISOString() : null,
                time_to: date && timeTo ? dayjs(`${date} ${timeTo}`).toISOString() : null,
                notes: getContractorOrderNotes(group.groupKey),
                documents: getContractorOrderDocuments(group.groupKey).map((document) => document.id),
            }
        })

        if (contractorOrders.some((contractorOrder) => !contractorOrder.time_from || !contractorOrder.time_to)) {
            return { error: true }
        }

        isSubmitting.value = true

        try {
            const response = await $lara.post($apiRoute(ordersStoreRoute, {
                organisation: organisationId,
                project: storedState.value.activeProjectId,
            }), {
                body: {
                    action: storedState.value.action,
                    containers: (storedState.value.selectedContainers ?? [])
                        .map((container) => ({
                            id: container.containerId,
                            group_key: container.groupKey,
                            container_count: container.containerCount,
                            notes: container.notes,
                            documents: (container.documents ?? []).map((document) => document.id),
                        })),
                    contractor_orders: contractorOrders,
                },
            })

            if (response?.error) {
                return response
            }

            const payload = response.data
            if (Array.isArray(payload)) {
                return { ...response, data: payload as OrderResource[] }
            }

            return { ...response, data: [payload as OrderResource] }
        } finally {
            isSubmitting.value = false
        }
    }

    async function fetchContainers(organisationId: number | null, projectId: number | null) {
        if (!organisationId || !projectId) {
            containers.value = []
            containersStatus.value = 'idle'
            lastContainersFetch.value = { organisationId, projectId }
            return
        }

        if (containersStatus.value === 'pending') {
            return
        }

        if (lastContainersFetch.value.organisationId === organisationId
            && lastContainersFetch.value.projectId === projectId
            && containersStatus.value === 'success') {
            return
        }

        containersStatus.value = 'pending'
        const containerIndexRoute = $can('viewAny', 'organisations')
            ? 'spa.system.organisations.projects.containers.index'
            : 'spa.projects.organisations.projects.containers.index'

        const response = await $lara.get($apiRoute(containerIndexRoute, {
            organisation: organisationId,
            project: projectId,
        }), {
            params: { perPage: 999 },
        })

        lastContainersFetch.value = { organisationId, projectId }

        if (response.error) {
            containers.value = []
            containersStatus.value = 'error'
            return
        }

        containers.value = response.data ?? []
        containersStatus.value = 'success'
        pruneSelectedContainers(containers.value)
    }

    async function fetchActiveProject(organisationId: number | null, projectId: number | null) {
        if (!organisationId || !projectId) {
            activeProject.value = null
            activeProjectStatus.value = 'idle'
            lastActiveProjectFetch.value = { organisationId, projectId }
            return
        }

        if (activeProjectStatus.value === 'pending') {
            return
        }

        if (lastActiveProjectFetch.value.organisationId === organisationId
            && lastActiveProjectFetch.value.projectId === projectId
            && activeProjectStatus.value === 'success') {
            return
        }

        activeProjectStatus.value = 'pending'
        const projectShowRoute = $can('viewAny', 'organisations')
            ? 'spa.system.organisations.projects.show'
            : 'spa.projects.organisations.projects.show'

        const response = await $lara.get($apiRoute(projectShowRoute, {
            organisation: organisationId,
            project: projectId,
        }))

        lastActiveProjectFetch.value = { organisationId, projectId }

        if (response.error) {
            const responseStatus = response.error.response?.status ?? null

            if (projectId === storedState.value.activeProjectId && [404, 422].includes(responseStatus ?? 0)) {
                await clearInvalidActiveProject()
                return
            }

            activeProject.value = null
            activeProjectStatus.value = 'error'
            return
        }

        activeProject.value = response.data ?? null
        activeProjectStatus.value = 'success'
    }

    const activeProjectName = computed(() => activeProject.value?.name || '-')

    const organisationId = computed(() => authStore.userData?.organisation_id ?? null)

    watch([organisationId, activeProjectId], () => {
        fetchActiveProject(organisationId.value, activeProjectId.value)
        fetchContainers(organisationId.value, activeProjectId.value)
    }, { immediate: true })

    watch(selectedContractorOrderGroups, (groups) => {
        pruneContractorOrders(groups)
    }, { immediate: true })

    watch([
        () => authStore.isAuthenticated,
        () => authStore.userData?.id ?? null,
        () => authStore.userData?.organisation_id ?? null,
    ], () => {
        initFromAuth()
    }, { immediate: true })

    watch(() => useRoute().path, (routePath) => {
        const matchedStep = resolveStepFromRoute(routePath ?? '')
        if (!matchedStep || !isStepReachable(matchedStep)) {
            return
        }
        lastVisitedStep.value = matchedStep
    }, { immediate: true })

    return {
        stepRoutes,
        activeProjectId,
        action,
        selectedContainers,
        contractorOrders,
        lastVisitedStep,
        containers,
        containersStatus,
        activeProject,
        activeProjectStatus,
        isSubmitting,
        activeProjectName,
        currentStep,
        currentStepRoute,
        getRouteForStep,
        getReachableStep,
        getResumableRoute,
        isStepReachable,
        resolveStepFromRoute,
        initFromAuth,
        setActiveProject,
        setAction,
        addContainer,
        removeContainer,
        setContainerCount,
        isContainerSelected,
        getContainerCount,
        getContainerNotes,
        getContainerDocuments,
        getContractorOrderDate,
        getContractorOrderTimeFrom,
        getContractorOrderTimeTo,
        getContractorOrderNotes,
        getContractorOrderDocuments,
        selectedContainerCount,
        selectedContainersTotalCount,
        setContainerDetails,
        setContainerGroupKey,
        setContainerNotes,
        setContainerDocuments,
        setContractorOrderDetails,
        setContractorOrderDate,
        setContractorOrderTimeFrom,
        setContractorOrderTimeTo,
        setContractorOrderNotes,
        setContractorOrderDocuments,
        containerHasNotesOrDocuments,
        contractorOrderHasNotesOrDocuments,
        resetWizard,
        submitOrder,
        fetchContainers,
        fetchActiveProject,
    }
})
